import React, { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { matchPath, Route, Routes, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Device from '@/util/Device'
import scheduleIdle from '@/util/scheduleIdle'
import classNames from 'classnames'
import constants from '@/constants'
import routes from '@/routes'
import storage from '@/util/storage'
import { userIsAnonymous, getCredential } from '@/api/auth'
import { feedUrl } from '@/urls'
import { pathnameWithoutLocalePrefix } from '@/util/localePath'

import {
  fetchExchangeRates, 
  selectActiveCurrency,
  selectExchangeRateStatus,
  selectPageLayout, 
  setActiveCurrency,
  setAppLoading,
  setMarketType,
} from '@/store/app'
import {
  useABTests,
  useAppTheme,
  useCurrencies,
  useCurrentUser,
  useCurrentRouteName,
  useRealTimeAuthModel,
  useRealTimeMarket,
  useRealTimeSportsEvent,
} from '@/hooks'
import {
  fetchCurrenciesProproties,
  fetchCurrentUser,
  fetchUserInAllowedCountry,
  refreshToken,
  selectCurrentUserStatus,
  selectUserFromAllowedCountry,
  currentUserSuccess,
} from '@/store/account'
import { fetchUnreadNotificationCount, selectUnreadNotificationCountStatus } from '@/store/notifications'
import { urlSearchParams } from '@/util/urlSearchParams'

import { ToastContainer, Slide } from 'react-toastify'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import SuspensePanel from '@/components/suspense'
import ErrorHandler from '@/components/ui/error-handler'
import ScrollToTop from '@/components/ui/scroll-to-top'
import { NavigationProgressBar, NavigationReadyMarker } from '@/components/ui/navigation-progress'
import RouteController from '@/RouteController'
import { i18n } from '@/locale/i18n.js'
import NavbarSkeleton from '@/components/navbar/NavbarSkeleton'
import { useSiteControl } from '@/features/site-control/useSiteControl'

// Lazy loaded components
const Footer = lazy(() => import('@/layouts/Footer/Footer'))
const Navbar = lazy(() => import('@/components/navbar'))
const Tabbar = lazy(() => import('@/layouts/Tabbar/Tabbar'))
const LiveStrip = lazy(() => import('@/features/market/live-strip'))

/** Same loading state as Navbar when app is loading; used until Navbar has mounted */
function NavbarPlaceholder() {
  return <NavbarSkeleton />
}
const GlobalComponent = lazy(() => import('@/layouts/global-component'))
const ViewOffline = lazy(() => import('@/pages/offline'))
const OnboardingTour = lazy(() => import('@/features/onboarding-tour'))

const className = classNames('app', {
  'app--ios': Device.isIOS,
  'app--android': Device.isAndroid,
})

/** Pusher + wallet balance listeners — not needed for first paint. */
function AppRealtimeEffects() {
  const dispatch = useDispatch()
  const currentUser = useCurrentUser()
  useRealTimeMarket()
  useRealTimeSportsEvent()
  useRealTimeAuthModel({
    model: currentUser && currentUser.wallet ? currentUser.wallet : {},
    eventName: 'balance-changed',
    onReceiveMessage: () => {
      dispatch(fetchCurrentUser())
    },
  })
  return null
}

const App = () => {
  const { initTheme } = useAppTheme()
  const MAINTANANCE_MODE = false // deploying the new SSR, so sync is not instant
  const { fetchAndSyncTests } = useABTests()
  const isMobileSize = useIsMobileSize()
  // The bottom tabbar accompanies the mobile navbar, which applies through tablet (< lg 1024):
  // as soon as the navbar search disappears, the tabbar takes over the navigation
  const isTabbarSize = useIsMobileSize(1023)
  const pullToRefreshPx = usePullToRefresh(isMobileSize)
  const dispatch = useDispatch()
  const activeCurrency = useSelector(selectActiveCurrency)
  const location = useLocation()
  const currentLocation = useRef()
  const currentUser = useCurrentUser()
  const site = useSiteControl(currentUser?.id)
  const currentUserStatus = useSelector(selectCurrentUserStatus)
  const fromAllowedCountry = useSelector(selectUserFromAllowedCountry())
  const exchangeRate = useCurrencies()
  const unreadNotificationStatus = useSelector(selectUnreadNotificationCountStatus())
  const pageLayout = useSelector(selectPageLayout)
  const [isLoadingUser, setLoadingUser] = useState(true)
  const [navbarMounted, setNavbarMounted] = useState(false)
  /** Defer ToastContainer until after mount so SSR HTML matches client hydrate (avoids Toastify subtree mismatch). */
  const [toastHostReady, setToastHostReady] = useState(false)
  const exchangeRateStatus = useSelector(selectExchangeRateStatus())
  const [realtimeReady, setRealtimeReady] = useState(false)

  useEffect(() => {
    setNavbarMounted(true)
  }, [])

  useEffect(() => {
    setToastHostReady(true)
  }, [])

  useEffect(() => {
    return scheduleIdle(() => setRealtimeReady(true), 3000)
  }, [])

  const currentPage = useCurrentRouteName()
  const isAuthenticationRoute = () => location.pathname.startsWith('/l/')
  const isModalRoute = () => location.pathname.startsWith('/m/')
  const isPublicFollowUrl = () => matchPath({ path: '/:username/follow' }, location.pathname)
  const isNotificationPage = () => matchPath({ path: '/n/notifications' }, location.pathname)
  const isMarketPage = currentPage === 'Market page'

  /** Page the notification drawer was opened over. The drawer is a page route,
   *  not a modal, so without this the band under it unmounts and the navbar
   *  jumps as the drawer rises. Written after render, so on the render that
   *  lands on the drawer it still holds the page behind it. */
  const preNotificationPath = useRef(null)
  useEffect(() => {
    if (!isNotificationPage()) preNotificationPath.current = location.pathname
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    return scheduleIdle(() => {
      import('@/util/preloadHeavyRoutes').then(m => m.preloadWalletInteraction())
    }, 3000)
  }, [])

  //  initialize error service (once at startup, after first paint)
  useEffect(() => {
    return scheduleIdle(() => {
      import('@/services/errors/initErrorService').then((m) => m.default())
    }, 2500)
  }, [])

  //  initialize AB tests.
  useEffect(() => {
    return scheduleIdle(() => {
      fetchAndSyncTests()
    }, 2500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // fetchAndSyncTests is stable from useABTests hook

  //  initialize the app's theme.
  useEffect(() => {
    initTheme()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // initTheme is stable from useAppTheme hook

  //  manage the app's loading state.
  useEffect(() => {
    // Dispatches action to set app loading state based on various conditions.
    dispatch(setAppLoading(isLoadingUser || fromAllowedCountry === null || !exchangeRate))
  }, [dispatch, isLoadingUser, fromAllowedCountry, exchangeRate])

  // fetching user's country allowance status.
  useEffect(() => {
    dispatch(fetchUserInAllowedCountry())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // adding user ID to Google Tag Manager.
  useEffect(() => {
    if (!currentUser?.id) return
    return scheduleIdle(() => {
      import('@/services/analytics/analytics').then((m) =>
        m.default.addParamsToGTM({ user_id: currentUser.id })
      )
    }, 2500)
  }, [currentUser])

  useEffect(() => {
    if (exchangeRateStatus !== constants.IDLE) return
    return scheduleIdle(() => {
      dispatch(fetchExchangeRates())
    }, 2500)
  }, [dispatch, exchangeRateStatus])

  useEffect(() => {
    return scheduleIdle(() => {
      dispatch(fetchCurrenciesProproties())
    }, 2500)
  }, [dispatch])

  //   fetching unread notification count.
  useEffect(() => {
    if (!currentUser?.id || unreadNotificationStatus !== constants.IDLE) return
    return scheduleIdle(() => {
      dispatch(fetchUnreadNotificationCount())
    }, 2500)
  }, [dispatch, currentUser, unreadNotificationStatus])

  //  handle user authentication and currency settings based on country allowance.
  useEffect(() => {
    // Only proceed if fromAllowedCountry has been determined (i.e., is not null).
    if (fromAllowedCountry !== null) {
      userIsAnonymous().then(async isAnonymous => {
        // Proceed only if the user is not anonymous. [ there is a stored token in the local storage ]
        if (!isAnonymous) {
          // Retrieves the last real money currency used from storage.
          const lastRealMoneyCurrency = await storage('lastRealMoneyCurrency').get()
          // Retrieves the user's last currency choice from storage.
          const lastCurrency = await storage('userCurrency').get()
          const credential = await getCredential()
          // Only refresh when we have a refresh token (e.g. OAuth email/password).
          // Magic/SIWE may not return a refresh token; use existing access token to restore user.
          if (credential?.refreshToken) {
            await dispatch(refreshToken())
          } else {
            await dispatch(fetchCurrentUser())
          }
          // Play money is the default for every user, regardless of country.
          const defaultCurrency = 'OOM'
          // Sets the active currency if no last currency settings are found.
          if (!lastRealMoneyCurrency && !lastCurrency) {
            dispatch(setActiveCurrency(defaultCurrency))
          }
        } else {
          const lastCurrency = await storage('userCurrency').get()
          const defaultCurrency = 'OOM'
          const invalidStored = lastCurrency === constants.REAL_MONEY

          const currency =
            lastCurrency === 'OOM'
              ? 'OOM'
              : lastCurrency && !invalidStored
                ? lastCurrency
                : defaultCurrency

          dispatch(setActiveCurrency(currency))
          dispatch(
            setMarketType(currency === 'OOM' ? constants.PLAY_MONEY : constants.REAL_MONEY)
          )
        }
        // Mark current user as resolved with no user so place-bet shows Login instead of disabled Bet
        if (isAnonymous) {
          dispatch(currentUserSuccess(null))
        }
        // Sets the loading user state to false, indicating completion of the process.
        setLoadingUser(false)
      })
    }
  }, [dispatch, fromAllowedCountry]) // This effect runs when fromAllowedCountry changes.

  // parse and store URL parameters for analytics.
  useEffect(() => {
    // Creates a new URLSearchParams object to parse the query string of the URL.
    const urlStructure = new urlSearchParams(window.location.search)
    // List of analytics parameters to look for in the URL.
    const analyticsParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
    // Object to store the found analytics parameters.
    let output = {}
    // Iterates over the analyticsParams array.
    analyticsParams.forEach(key => {
      // Retrieves the value for each analytics parameter in the URL, if present.
      const content = urlStructure.get(key)

      // If the parameter is found in the URL, add it to the output object.
      if (content) output[key] = content
    })
    // Converts the output object into a JSON string.
    const strOutput = JSON.stringify(output)
    // Stores the JSON string of analytics parameters in local storage under 'utms'.
    storage('utms').set(strOutput)
  }, [])
  useEffect(() => {
    setTimeout(() => {
      // if otheres fail we default to 30 seconds
      if (!window.prerenderReady) window.prerenderReady = true
    }, 30000)
  }, [])

  useEffect(() => {
    if (!currentUser || pageLayout.appLoading) return

    const getUserCurrency = currency => {
      // If the currency selected is zero we should switch to another one
      if (currency === 'OOM' || currentUser.wallet[currency]) return currency
      for (let key in currentUser.wallet) {
        if (key !== 'OOM' && currentUser.wallet[key] !== 0) {
          return { key: key, value: currentUser.wallet[key] }
        }
      }
      return null
    }

    storage('userCurrency')
      .get()
      .then(async storedCurrency => {
        const defaultCurrency = 'OOM'

        // Check if storedCurrency is a placeholder value
        const isStoredCurrencyPlaceholder =
          storedCurrency === constants.REAL_MONEY || storedCurrency === constants.REAL_MONEY_PLACEHOLDER

        // Determine currency with proper priority
        let currency

        if (!isStoredCurrencyPlaceholder && storedCurrency) {
          // Case 1: We have a stored currency that's not a placeholder - highest priority
          currency = storedCurrency
        } else if (currentUser && getUserCurrency(currentUser.settings.last_currency_selected)) {
          // Case 2: Use the user's last selected currency - second priority
          currency = getUserCurrency(currentUser.settings.last_currency_selected)
        } else if (isStoredCurrencyPlaceholder && storedCurrency) {
          // Case 3: We have a stored currency that IS a placeholder - third priority
          currency = storedCurrency
        } else {
          // Case 4: Fallback to play money - lowest priority
          currency = defaultCurrency
        }
        const marketType = currency === 'OOM' ? constants.PLAY_MONEY : constants.REAL_MONEY

        dispatch(setActiveCurrency(currency))
        dispatch(setMarketType(marketType))
      })
  }, [dispatch, pageLayout.appLoading, currentUser])

  useEffect(() => {
    if (activeCurrency !== 'OOM') {
      dispatch(fetchExchangeRates())
    }
    const interval = setInterval(() => {
      if (activeCurrency !== 'OOM') {
        dispatch(fetchExchangeRates())
      }
    }, 1000 * 60)

    return () => {
      clearInterval(interval)
    }
  }, [dispatch, activeCurrency])

  // Identify user in Mixpanel after they're loaded
  useEffect(() => {
    if (!currentUser?.id) return
    return scheduleIdle(() => {
      import('@/services/analytics/Mixpanel').then((m) => m.default.identify(currentUser.id))
    }, 2500)
  }, [currentUser])

  const isModalPath = pathname =>
    routes.some(route => {
      if (!route.isModal) return false
      const paths = Array.isArray(route.path) ? route.path : [route.path]
      return paths.some(path => matchPath(path, pathname))
    })

  /** Path of the page under a modal (login, deposit, …), otherwise the current URL. */
  const backgroundPathname = () => {
    if (!isModalPath(location.pathname)) return location.pathname
    const fromRef = currentLocation.current?.pathname
    if (fromRef) return String(fromRef).split('?')[0]
    if (typeof sessionStorage === 'undefined') return feedUrl()
    const saved = sessionStorage.getItem('lastNonModalPath')
    return (saved || feedUrl()).split('?')[0]
  }

  /** The live activity band belongs on the home feed, including when a modal or
   *  the notification drawer sits on top of it. */
  const isHomeRoute = () => {
    const path =
      isNotificationPage() && preNotificationPath.current
        ? preNotificationPath.current
        : backgroundPathname()
    return pathnameWithoutLocalePrefix(path) === feedUrl()
  }

  // NAVBAR
  const shouldDisplayNavbar = () => {
    if (Device.isMobile) {
      return !isAuthenticationRoute() && !isModalRoute() && !isPublicFollowUrl()
    }
    return true
  }

  // Market detail keeps its sticky bet buttons on the bottom edge instead of the tab bar.
  const shouldDisplayTabbar = () => {
    return !isAuthenticationRoute() && !isModalRoute() && !isNotificationPage() && !isMarketPage
  }

  const renderRoutes = routes => {
    return routes.map(route => {
      if (Array.isArray(route.path)) {
        return route.path.map((p, idx) => {
          return <Route key={`${p}-${idx}`} path={p} element={route.element} />
        })
      } else {
        return <Route key={`route-to-${route.path}`} path={route.path} element={route.element} />
      }
    })
  }

  const forcedOffline = MAINTANANCE_MODE || location.pathname === '/offline'
  const siteWall = site.closed || forcedOffline
  const keepAuthOpen = site.closed && !forcedOffline && (
    location.pathname === '/l' ||
    location.pathname.startsWith('/l/') ||
    location.pathname === '/callback' ||
    location.pathname === '/p/complete-your-profile' ||
    location.pathname === '/p/verify-your-email'
  )
  if (siteWall && !keepAuthOpen) {
    return (
      <Suspense fallback={<SuspensePanel page />}>
        <ViewOffline
          message={site.closed ? site.message : ''}
          allowStaffSignIn={site.closed && site.audience === 'public'}
        />
      </Suspense>
    )
  }

  const RenderAppSwitch = () => {
    const location = useLocation()
    const isServer = typeof window === 'undefined'

    const pageRoutes = routes.filter(route => !route.isModal)
    const modalRoutes = routes.filter(route => route.isModal)

    const isModal = modalRoutes.find(route => {
      if (Array.isArray(route.path)) {
        return route.path.some(path => matchPath(path, location.pathname))
      }
      return matchPath(route.path, location.pathname)
    })

    let modalLocation = { ...location }

    if (isModal) {
      if (currentLocation.current) {
        const { pathname, search } = currentLocation.current
        const returnUrl = pathname + (search || '')
        modalLocation = { ...modalLocation, state: { returnUrl } }
      } else {
        const savedPath = sessionStorage.getItem('lastNonModalPath') || feedUrl()
        const qIndex = savedPath.indexOf('?')
        const savedPathname = qIndex === -1 ? savedPath : savedPath.slice(0, qIndex)
        const savedSearch = qIndex === -1 ? '' : savedPath.slice(qIndex)
        currentLocation.current = { ...location, pathname: savedPathname, search: savedSearch }
        modalLocation = { ...modalLocation, state: { returnUrl: savedPath } }
      }
    } else {
      currentLocation.current = location
      sessionStorage.setItem('lastNonModalPath', location.pathname + (location.search || ''))
    }

    // SSR: only render page routes to avoid "No routes matched" and invalid element from modal tree
    if (isServer) {
      return (
        <Suspense fallback={<SuspensePanel page />}>
          <Routes location={currentLocation.current}>{renderRoutes(pageRoutes)}</Routes>
        </Suspense>
      )
    }

    return (
      <>
        <Suspense fallback={null}>
          <Routes location={modalLocation}>{renderRoutes(modalRoutes)}</Routes>
        </Suspense>
        <Suspense fallback={<SuspensePanel page />}>
          <NavigationReadyMarker />
          <Routes location={currentLocation.current}>{renderRoutes(pageRoutes)}</Routes>
        </Suspense>
      </>
    )
  }

  const content = (
    <>
      {realtimeReady ? <AppRealtimeEffects /> : null}
      <ScrollToTop />
      <NavigationProgressBar />
      {isMobileSize && pullToRefreshPx > 4 ? (
        <div
          className="fixed left-0 right-0 top-0 z-[100060] pointer-events-none h-1 bg-[var(--color-brand-04)] origin-left transition-transform"
          style={{
            transform: `scaleX(${Math.min(pullToRefreshPx / 72, 1)})`,
            marginTop: 'env(safe-area-inset-top, 0px)',
          }}
          aria-hidden
        />
      ) : null}
      {toastHostReady ? (
        <ToastContainer stacked={isMobileSize} position="top-right" transition={Slide} style={{ zIndex: 100070 }} />
      ) : null}
      <div className={className}>
        <ErrorHandler location={location} component="LiveStrip">
          {isHomeRoute() ? (
            <Suspense fallback={null}>
              <LiveStrip />
            </Suspense>
          ) : null}
        </ErrorHandler>

        <ErrorHandler location={location} component="Navbar">
          {shouldDisplayNavbar() ? (
            navbarMounted ? (
              <Suspense fallback={<NavbarPlaceholder />}>
                <Navbar me={currentUser} />
              </Suspense>
            ) : (
              <NavbarPlaceholder />
            )
          ) : null}
        </ErrorHandler>

        <ErrorHandler location={location} component="AppRouter">{RenderAppSwitch()}</ErrorHandler>

        <ErrorHandler location={location} component="Tabbar">
          {isTabbarSize && shouldDisplayTabbar() ? (
            <Suspense fallback={null}>
              <Tabbar me={currentUser} />
            </Suspense>
          ) : null}
        </ErrorHandler>
      </div>
      <Suspense fallback={null}>
        <GlobalComponent />
      </Suspense>
      <ErrorHandler location={location} component="Footer">
        <div className={isAuthenticationRoute() || isModalRoute() ? 'hidden' : undefined} aria-hidden={isAuthenticationRoute() || isModalRoute()}>
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </div>
      </ErrorHandler>
      <ErrorHandler location={location} component="OnboardingTour">
        {currentUser &&
          currentUser.settings &&
          !currentUser.settings.seen_onboarding_app &&
          currentUser.username_confirmed && (
            <Suspense fallback={<div className="loading-placeholder">{i18n('onboarding_tour.loading_message')}</div>}>
              <OnboardingTour isFromAllowedCountry={fromAllowedCountry} isEmailVerified={currentUser.email_confirmed} />
            </Suspense>
          )}
      </ErrorHandler>
    </>
  )

  return (
    <RouteController>
      {content}
    </RouteController>
  )
}

export default App
